import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { portalApi, tournamentApi } from '../../services/api'
import PortalShell from './PortalShell'
import { isOnboardingComplete } from './onboardingStatus'
import { useLanguage } from '../../context/LanguageContext'
import { stripMarkdown } from '../../components/MarkdownField'

// Layouts/styles built as functions so name+desc can be translated.
function buildLayouts(t) {
  return [
    { value: 'cards',      name: t('sp_layout_cards', 'Cards'),      desc: t('sp_layout_cards_desc', 'Equal-size grid') },
    { value: 'comparison', name: t('sp_layout_comparison', 'Comparison'), desc: t('sp_layout_comparison_desc', 'Side-by-side columns') },
    { value: 'hero',       name: t('sp_layout_hero', 'Hero'),       desc: t('sp_layout_hero_desc', 'Bordered cards + side price') },
    { value: 'minimal',    name: t('sp_layout_minimal', 'Minimal'),    desc: t('sp_layout_minimal_desc', 'Clean list with accent') },
    { value: 'featured',   name: t('sp_layout_featured', 'Featured'),   desc: t('sp_layout_featured_desc', 'One large + smaller cards') },
    { value: 'gallery',    name: t('sp_layout_gallery', 'Gallery'),    desc: t('sp_layout_gallery_desc', 'Image-first grid') },
    { value: 'carousel',   name: t('sp_layout_carousel', 'Carousel'),   desc: t('sp_layout_carousel_desc', 'Horizontal scroll strip') },
    { value: 'split',      name: t('sp_layout_split', 'Split'),      desc: t('sp_layout_split_desc', 'Alternating image/content') },
  ]
}
function buildStyles(t) {
  return [
    { value: 'inherit', name: t('sp_style_match_site', 'Match Site'), desc: t('sp_style_match_site_desc', 'Use site fonts'), sample: { headerFont: null, bodyFont: null } },
    { value: 'classic', name: t('sp_style_classic', 'Classic'), desc: 'Playfair + Lora', sample: { headerFont: 'Playfair Display', bodyFont: 'Lora', headerWeight: 700, headerCase: 'normal' } },
    { value: 'modern', name: t('sp_style_modern', 'Modern'), desc: 'Montserrat + Open Sans', sample: { headerFont: 'Montserrat', bodyFont: 'Open Sans', headerWeight: 700, headerCase: 'normal' } },
    { value: 'bold', name: t('sp_style_bold', 'Bold'), desc: t('sp_style_bold_desc', 'Oswald upper + Inter'), sample: { headerFont: 'Oswald', bodyFont: 'Inter', headerWeight: 600, headerCase: 'uppercase', headerLetterSpacing: '0.04em' } },
    { value: 'minimal', name: t('sp_layout_minimal', 'Minimal'), desc: t('sp_style_minimal_desc', 'Inter / Inter, light'), sample: { headerFont: 'Inter', bodyFont: 'Inter', headerWeight: 500, headerCase: 'normal' } },
    { value: 'editorial', name: t('sp_style_editorial', 'Editorial'), desc: 'Cormorant + Crimson', sample: { headerFont: 'Cormorant Garamond', bodyFont: 'Crimson Text', headerWeight: 700, headerCase: 'normal' } },
    { value: 'custom', name: t('sp_custom', 'Custom'), desc: t('sp_style_custom_desc', 'Pull from website'), sample: {} },
  ]
}

export default function BrandingSetup() {
  const { t } = useLanguage()
  const LAYOUTS = buildLayouts(t)
  const STYLES = buildStyles(t)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedAt, setSavedAt] = useState(null)
  const [branding, setBranding] = useState({
    logoUrl: '',
    layout: 'cards',
    style: 'inherit',
    headerFont: '',
    bodyFont: '',
    headerWeight: 700,
    headerCase: 'normal',
    headerLetterSpacing: 'normal',
    primaryColor: '',
    accentColor: '',
    secondaryColor: '',
    sourceUrl: '',
    // Featured image — only used when layout is 'featured'. Displayed as
    // the hero image on the featured package slot. Falls back to the
    // first package's HeroImageUrl if not set.
    featuredImageUrl: '',
    // Backup fields from the most recent AI URL extract. These survive
    // preset switches so clicking Custom later restores the pulled values.
    extractedHeaderFont: '',
    extractedBodyFont: '',
    extractedPrimaryColor: '',
    extractedAccentColor: '',
    extractedSecondaryColor: '',
  })

  // Featured image upload state
  const [uploadingFeatured, setUploadingFeatured] = useState(false)
  const [featuredUploadError, setFeaturedUploadError] = useState('')

  // URL extractor state
  const [extractUrl, setExtractUrl] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [extractResult, setExtractResult] = useState(null)
  const [extractError, setExtractError] = useState('')

  // Real packages (for the live preview — pulls from the merchant's actual data)
  const [packages, setPackages] = useState([])

  // Status tracking — used to hide the "STEP X OF 4" banner once the
  // merchant has finished all four onboarding steps
  const [status, setStatus] = useState(null)

  // Load existing branding + packages
  useEffect(() => {
    Promise.all([
      portalApi.get('/onboarding/status')
        .then(({ data }) => {
          const b = data.settings?.branding || {}
          setBranding(prev => ({ ...prev, ...b }))
          if (b.sourceUrl) setExtractUrl(b.sourceUrl)
          setStatus(data)
        })
        .catch(() => {}),
      tournamentApi.get('/play-stay/packages')
        .then(({ data }) => {
          // Active packages first; the live preview cycles through them
          const list = Array.isArray(data) ? data.filter(p => p.IsActive) : []
          setPackages(list.length ? list : (Array.isArray(data) ? data : []))
        })
        .catch(() => setPackages([])),
    ]).finally(() => setLoading(false))
  }, [])

  function set(key, val) {
    setBranding(b => ({ ...b, [key]: val }))
    setSavedAt(null)
  }

  function applyStylePreset(styleVal) {
    const preset = STYLES.find(s => s.value === styleVal)
    if (!preset) return
    if (styleVal === 'inherit') {
      // Clear active fonts to fall back to the embedding site's defaults,
      // BUT keep extractedFonts intact so clicking Custom later restores them.
      setBranding(b => ({
        ...b,
        style: 'inherit',
        headerFont: '',
        bodyFont: '',
      }))
    } else if (styleVal === 'custom') {
      // Restore from the extracted backup if one exists — this is what makes
      // "pulled from website, clicked another preset, clicked Custom again"
      // actually bring back your Cormorant Garamond instead of being blank.
      setBranding(b => ({
        ...b,
        style: 'custom',
        ...(b.extractedHeaderFont ? { headerFont: b.extractedHeaderFont } : {}),
        ...(b.extractedBodyFont ? { bodyFont: b.extractedBodyFont } : {}),
        ...(b.extractedPrimaryColor ? { primaryColor: b.extractedPrimaryColor } : {}),
        ...(b.extractedAccentColor ? { accentColor: b.extractedAccentColor } : {}),
        ...(b.extractedSecondaryColor ? { secondaryColor: b.extractedSecondaryColor } : {}),
      }))
    } else {
      setBranding(b => ({
        ...b,
        style: styleVal,
        headerFont: preset.sample.headerFont || '',
        bodyFont: preset.sample.bodyFont || '',
        headerWeight: preset.sample.headerWeight || 700,
        headerCase: preset.sample.headerCase || 'normal',
        headerLetterSpacing: preset.sample.headerLetterSpacing || 'normal',
      }))
    }
    setSavedAt(null)
  }

  async function handleExtract() {
    if (!extractUrl.trim()) {
      setExtractError('Enter your course website URL first')
      return
    }
    setExtracting(true)
    setExtractError('')
    setExtractResult(null)
    try {
      const { data } = await tournamentApi.post('/course-website/extract-styles', { url: extractUrl.trim() })
      setExtractResult(data)
      if (data.source === 'failed') {
        setExtractError(data.message || 'Could not extract styles from that URL')
      } else {
        // Apply extracted values + switch to custom mode. ALSO save them under
        // `extracted*` backup fields that survive preset switches — so if the
        // merchant later clicks another preset and then returns to Custom, the
        // AI-extracted values are still available to restore from.
        // Always overwrite ALL extracted fields — if the new site doesn't
        // have an accent color, clear the old one so stale values don't linger.
        setBranding(b => ({
          ...b,
          style: 'custom',
          sourceUrl: data.url,
          headerFont: data.headerFont || '',
          bodyFont: data.bodyFont || '',
          primaryColor: data.primaryColor || '',
          accentColor: data.accentColor || '',
          secondaryColor: data.secondaryColor || '',
          extractedHeaderFont: data.headerFont || '',
          extractedBodyFont: data.bodyFont || '',
          extractedPrimaryColor: data.primaryColor || '',
          extractedAccentColor: data.accentColor || '',
          extractedSecondaryColor: data.secondaryColor || '',
        }))
        setSavedAt(null)
      }
    } catch (err) {
      setExtractError(err.response?.data?.message || err.message || 'Extraction failed')
    } finally {
      setExtracting(false)
    }
  }

  async function handleSave() {
    setSaving(true)
    setError('')
    try {
      await portalApi.post('/onboarding/branding', branding)
      navigate('/portal')
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  async function handleFeaturedImageUpload(file) {
    if (!file) return
    setUploadingFeatured(true)
    setFeaturedUploadError('')
    try {
      const formData = new FormData()
      formData.append('image', file)
      const { data } = await tournamentApi.post('/course-website/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // Tournament-API returns a site-relative URL like /site-assets/uploads/hero-abc.jpg
      // Store the absolute URL so the widget can render it when embedded on a third-party site.
      // Build an absolute URL so the image works when embedded on a
      // third-party site. Use VITE_API_ORIGIN env var so it works on
      // any machine (3001 here, 4001 on Todd's other machine, real
      // domain in production). Falls back to same-origin for dev.
      const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
      const fullUrl = data.url.startsWith('http') ? data.url : apiOrigin + data.url
      setBranding(b => ({ ...b, featuredImageUrl: fullUrl }))
      setSavedAt(null)
    } catch (err) {
      setFeaturedUploadError(err.response?.data?.message || err.message || t('sp_upload_failed', 'Upload failed'))
    } finally {
      setUploadingFeatured(false)
    }
  }

  return (
    <PortalShell>
      <div style={{ maxWidth: 1000, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 28 }}>
          {!isOnboardingComplete(status, packages) && (
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--brand-600)', fontWeight: 700, marginBottom: 6 }}>
              {t('sp_setup_step_4_branding_caps', 'SETUP · STEP 4 OF 4 · BRANDING')}
            </div>
          )}
          <h1 style={{ margin: 0, fontSize: 28, color: 'var(--secondary-950)' }}>{t('sp_brand_widget', 'Brand your booking widget')}</h1>
          <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 15 }}>
            {t('sp_branding_blurb', 'Pick a layout and style for your Stay & Play widget. Use "Match my website" to auto-pull your existing brand fonts and colors with AI.')}
          </p>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading_ellipsis', 'Loading...')}</div>
        ) : (
          <>
            {error && (
              <div style={errorBoxStyle}>{error}</div>
            )}

            {/* ── Match my website (URL pull) ── */}
            <div style={extractBoxStyle}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16 }} aria-hidden="true">✨</span>
                {t('sp_match_my_website_ai', 'Match my website (AI-powered)')}
              </div>
              <div style={{ fontSize: 12, color: '#78350f', marginBottom: 10, lineHeight: 1.4 }}>
                {t('sp_match_website_blurb', 'Paste your existing course website URL and we\'ll use AI to pull your brand fonts and colors automatically. You can edit any of the values after.')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={extractUrl}
                  onChange={e => setExtractUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleExtract() }}
                  placeholder="https://yourgolfcourse.com"
                  style={extractInputStyle}
                />
                <button
                  type="button"
                  onClick={handleExtract}
                  disabled={extracting}
                  style={extractBtnStyle}
                >
                  {extracting ? t('sp_analyzing', 'Analyzing...') : t('sp_pull_from_website', 'Pull from website')}
                </button>
              </div>
              {extractError && (
                <div style={extractErrStyle}>
                  <strong>{t('sp_fail_caps', 'FAIL')}</strong> · {extractError}
                </div>
              )}
              {extractResult && extractResult.source !== 'failed' && (
                <div style={extractOkStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <strong style={{ color: '#065f46' }}>{t('sp_ok_caps', 'OK')}</strong>
                    <span style={{ color: '#047857' }}>·</span>
                    <span style={{ color: '#047857', fontWeight: 600 }}>{extractResult.confidence} {t('sp_confidence_lower', 'confidence')}</span>
                    <span style={{ color: '#047857' }}>·</span>
                    <span style={{ color: '#6b7280' }}>{t('sp_analyzed_lower', 'analyzed')} {Math.round((extractResult.htmlBytesAnalyzed || 0) / 1024)} KB</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                    {extractResult.headerFont && <Pill label={t('sp_header_label', 'Header')} value={extractResult.headerFont} />}
                    {extractResult.bodyFont && <Pill label={t('sp_body_label', 'Body')} value={extractResult.bodyFont} />}
                    {extractResult.primaryColor && <ColorPill label={t('sp_primary_label', 'Primary')} hex={extractResult.primaryColor} />}
                    {extractResult.accentColor && <ColorPill label={t('sp_accent_label', 'Accent')} hex={extractResult.accentColor} />}
                    {extractResult.secondaryColor && <ColorPill label={t('sp_secondary_label', 'Secondary')} hex={extractResult.secondaryColor} />}
                  </div>
                  {extractResult.notes && (
                    <div style={{ color: '#475569', fontSize: 11, marginTop: 6, fontStyle: 'italic' }}>{extractResult.notes}</div>
                  )}
                </div>
              )}
            </div>

            {/* ── Logo URL ── */}
            <div className="card" style={cardStyle}>
              <h3 style={sectionTitleStyle}>{t('sp_logo', 'Logo')}</h3>
              <p style={hintStyle}>{t('sp_logo_hint', 'Paste a URL to your logo. Used in the widget header and confirmation emails.')}</p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input
                  type="url"
                  value={branding.logoUrl || ''}
                  onChange={e => set('logoUrl', e.target.value)}
                  placeholder="https://yoursite.com/logo.png"
                  style={textInputStyle}
                />
                {branding.logoUrl && (
                  <div style={{ flexShrink: 0, padding: 8, background: '#f8fafc', border: '1px solid var(--card-border)', borderRadius: 6 }}>
                    <img src={branding.logoUrl} alt="logo preview" style={{ height: 36, maxWidth: 140, objectFit: 'contain' }}
                      onError={e => { e.target.style.display = 'none' }} />
                  </div>
                )}
              </div>
            </div>

            {/* ── Layout picker ── */}
            <div className="card" style={cardStyle}>
              <h3 style={sectionTitleStyle}>{t('sp_layout_h', 'Layout')}</h3>
              <p style={hintStyle}>{t('sp_layout_hint', 'How packages are arranged on the page.')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
                {LAYOUTS.map(l => (
                  <button
                    key={l.value}
                    type="button"
                    onClick={() => set('layout', l.value)}
                    style={{
                      ...pickerCardStyle,
                      padding: '10px 8px',
                      borderColor: branding.layout === l.value ? 'var(--brand-500)' : 'var(--card-border)',
                      background: branding.layout === l.value ? 'var(--brand-50)' : '#fff',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 12, color: branding.layout === l.value ? 'var(--brand-700)' : 'var(--secondary-950)' }}>{l.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.2 }}>{l.desc}</div>
                  </button>
                ))}
              </div>

              {/* Featured image upload — only shown when featured layout is selected */}
              {branding.layout === 'featured' && (
                <div style={{
                  marginTop: 16, padding: 16,
                  background: '#f8fafc', border: '1px solid var(--card-border)', borderRadius: 8,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 4 }}>
                    {t('sp_featured_image', 'Featured image')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10 }}>
                    {t('sp_featured_image_hint', 'Upload a hero image for your featured package. Max 10MB. JPG, PNG, WebP, or GIF.')}
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {branding.featuredImageUrl ? (
                      <div style={{
                        width: 180, height: 120,
                        background: `url('${branding.featuredImageUrl}') center/cover no-repeat`,
                        borderRadius: 6, flexShrink: 0,
                        border: '1px solid var(--card-border)',
                      }} />
                    ) : (
                      <div style={{
                        width: 180, height: 120,
                        background: '#f1f5f9',
                        borderRadius: 6, flexShrink: 0,
                        border: '1px dashed var(--card-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: 'var(--text-muted)',
                      }}>
                        {t('sp_no_image', 'No image')}
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <label
                        className="btn btn-outline-dark"
                        style={{ padding: '10px 20px', cursor: uploadingFeatured ? 'wait' : 'pointer', display: 'inline-block' }}
                      >
                        {uploadingFeatured ? t('sp_uploading', 'Uploading...') : (branding.featuredImageUrl ? t('sp_replace_image', 'Replace image') : t('sp_upload_image', 'Upload image'))}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={e => handleFeaturedImageUpload(e.target.files?.[0])}
                          disabled={uploadingFeatured}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {branding.featuredImageUrl && (
                        <button
                          type="button"
                          onClick={() => set('featuredImageUrl', '')}
                          style={{
                            marginLeft: 8, padding: '10px 16px',
                            background: 'transparent', color: '#dc2626',
                            border: '1px solid #fecaca', borderRadius: 6,
                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {t('sp_remove', 'Remove')}
                        </button>
                      )}
                      {featuredUploadError && (
                        <div style={{ marginTop: 8, padding: '6px 10px', background: '#fef2f2', color: '#991b1b', borderRadius: 4, fontSize: 11, border: '1px solid #fca5a5' }}>
                          <strong>{t('sp_fail_caps', 'FAIL')}</strong> · {featuredUploadError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Package images — shown for any layout that renders a per-package
                  image slot in the embed (renderPackages in stayPlayEmbed.js).
                  Hero layout was missing here even though the embed reads
                  packageImages[pkg.ID_NO] for it — merchants had no way to upload. */}
              {['gallery', 'carousel', 'split', 'hero'].includes(branding.layout) && packages.length > 0 && (
                <div style={{
                  marginTop: 16, padding: 16,
                  background: '#f8fafc', border: '1px solid var(--card-border)', borderRadius: 8,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--secondary-950)', marginBottom: 4 }}>
                    {t('sp_package_images', 'Package images')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
                    {t('sp_package_images_hint_pre', 'Upload an image for each package. These show as the hero image in the')} {branding.layout} {t('sp_package_images_hint_post', 'layout.')}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {packages.map(pkg => {
                      const imgUrl = (branding.packageImages || {})[pkg.ID_NO] || ''
                      return (
                        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
                          display: 'flex', gap: 12, alignItems: 'center',
                          padding: 10, background: '#fff', borderRadius: 8,
                          border: '1px solid var(--card-border)',
                        }}>
                          {/* Thumbnail */}
                          {imgUrl ? (
                            <div style={{
                              width: 120, height: 80, flexShrink: 0, borderRadius: 6,
                              background: `url('${imgUrl}') center/cover no-repeat`,
                              border: '1px solid var(--card-border)',
                            }} />
                          ) : (
                            <div style={{
                              width: 120, height: 80, flexShrink: 0, borderRadius: 6,
                              background: '#f1f5f9', border: '1px dashed var(--card-border)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 10, color: 'var(--text-muted)',
                            }}>{t('sp_no_image', 'No image')}</div>
                          )}
                          {/* Package name + actions */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--secondary-950)', marginBottom: 6 }}>
                              {pkg.PackageName}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <label style={{
                                padding: '5px 12px', fontSize: 11, fontWeight: 600,
                                background: '#fff', border: '1px solid var(--card-border)', borderRadius: 4,
                                cursor: 'pointer',
                              }}>
                                {imgUrl ? t('sp_replace', 'Replace') : t('sp_upload', 'Upload')}
                                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (!file) return
                                    try {
                                      const formData = new FormData()
                                      formData.append('image', file)
                                      const { data } = await tournamentApi.post('/course-website/upload-image', formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                      })
                                      const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
                                      const fullUrl = data.url.startsWith('http') ? data.url : apiOrigin + data.url
                                      setBranding(b => ({
                                        ...b,
                                        packageImages: { ...(b.packageImages || {}), [pkg.ID_NO]: fullUrl },
                                      }))
                                      setSavedAt(null)
                                    } catch (err) {
                                      setError(err.response?.data?.message || t('sp_upload_failed', 'Upload failed'))
                                    }
                                    e.target.value = ''
                                  }}
                                />
                              </label>
                              {imgUrl && (
                                <button type="button"
                                  onClick={() => {
                                    setBranding(b => {
                                      const imgs = { ...(b.packageImages || {}) }
                                      delete imgs[pkg.ID_NO]
                                      return { ...b, packageImages: imgs }
                                    })
                                    setSavedAt(null)
                                  }}
                                  style={{
                                    padding: '5px 12px', fontSize: 11, fontWeight: 600,
                                    background: '#fff', color: '#dc2626',
                                    border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer',
                                  }}>
                                  {t('sp_remove', 'Remove')}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ── Style picker ── */}
            <div className="card" style={cardStyle}>
              <h3 style={sectionTitleStyle}>{t('sp_style_section_title', 'Style (Fonts & Text)')}</h3>
              <p style={hintStyle}>{t('sp_style_hint', 'Typography preset. Pick "Match Site" if you\'ll embed inside your existing course website.')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10 }}>
                {STYLES.map(s => {
                  const active = branding.style === s.value
                  return (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => applyStylePreset(s.value)}
                      style={{
                        ...pickerCardStyle,
                        padding: '10px 8px',
                        borderColor: active ? 'var(--brand-500)' : 'var(--card-border)',
                        background: active ? 'var(--brand-50)' : '#fff',
                      }}
                    >
                      <div style={{
                        fontFamily: s.sample?.headerFont ? `'${s.sample.headerFont}', serif` : 'inherit',
                        fontWeight: s.sample?.headerWeight || 700,
                        fontSize: 14,
                        textTransform: s.sample?.headerCase || 'none',
                        letterSpacing: s.sample?.headerLetterSpacing || 0,
                        color: active ? 'var(--brand-700)' : '#1e293b',
                        lineHeight: 1.1,
                        marginBottom: 4,
                      }}>Aa Bb</div>
                      <div style={{ fontSize: 11, fontWeight: active ? 700 : 600, color: active ? 'var(--brand-700)' : 'var(--secondary-950)' }}>{s.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.2 }}>{s.desc}</div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ── Custom mode: manual font + color overrides ── */}
            {branding.style === 'custom' && (
              <div className="card" style={cardStyle}>
                <h3 style={sectionTitleStyle}>{t('sp_custom_typo_colors', 'Custom typography & colors')}</h3>
                <p style={hintStyle}>{t('sp_custom_typo_hint', 'Set your own fonts and colors. Pre-filled by the URL extractor above.')}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={fieldLabelStyle}>{t('sp_header_font', 'Header font')}</label>
                    <input type="text" value={branding.headerFont || ''} onChange={e => set('headerFont', e.target.value)}
                      placeholder="e.g. Montserrat" style={textInputStyle} />
                  </div>
                  <div>
                    <label style={fieldLabelStyle}>{t('sp_body_font', 'Body font')}</label>
                    <input type="text" value={branding.bodyFont || ''} onChange={e => set('bodyFont', e.target.value)}
                      placeholder="e.g. Open Sans" style={textInputStyle} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[
                    { key: 'primaryColor', label: t('sp_primary_label', 'Primary') },
                    { key: 'accentColor', label: t('sp_accent_buttons_links', 'Accent (buttons, links)') },
                    { key: 'secondaryColor', label: t('sp_secondary_label', 'Secondary') },
                  ].map(c => (
                    <div key={c.key}>
                      <label style={fieldLabelStyle}>{c.label}</label>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="color" value={branding[c.key] || '#000000'} onChange={e => set(c.key, e.target.value)}
                          style={{ width: 36, height: 32, border: 'none', padding: 0, cursor: 'pointer' }} />
                        <input type="text" value={branding[c.key] || ''} onChange={e => set(c.key, e.target.value)}
                          placeholder="#000000"
                          style={{ ...textInputStyle, fontFamily: 'monospace', fontSize: 12 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Live preview ── */}
            <div className="card" style={{ ...cardStyle, background: '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <h3 style={sectionTitleStyle}>{t('sp_embed_step3_preview', 'Preview')}</h3>
                {/* Show which colors are actually being applied so the merchant
                    can confirm their AI extraction or custom values landed */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
                  {branding.primaryColor && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: branding.primaryColor, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <span style={{ fontFamily: 'monospace' }}>{branding.primaryColor}</span>
                      <span style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>primary</span>
                    </span>
                  )}
                  {branding.accentColor && branding.accentColor !== branding.primaryColor && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 14, height: 14, borderRadius: 3, background: branding.accentColor, border: '1px solid rgba(0,0,0,0.1)' }} />
                      <span style={{ fontFamily: 'monospace' }}>{branding.accentColor}</span>
                      <span style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>accent</span>
                    </span>
                  )}
                </div>
              </div>
              <p style={hintStyle}>
                {t('sp_preview_blurb', 'All your active packages rendered together in the selected layout, with your fonts and colors applied. Primary drives titles and brand anchors; accent drives prices and CTAs.')}
              </p>
              {packages.length === 0 && (
                <div style={{
                  padding: 16, border: '1px dashed var(--card-border)', borderRadius: 8,
                  background: '#fffbeb', fontSize: 13, color: '#92400e', marginBottom: 10,
                }}>
                  <strong>{t('sp_no_packages_yet_strong', 'No packages yet.')}</strong> {t('sp_no_packages_preview_blurb', 'Create at least one package on the Packages page first so you can see your real pricing and inclusions in this preview.')}
                  <div style={{ marginTop: 8 }}>
                    <button type="button" onClick={() => navigate('/portal/packages')}
                      style={{
                        padding: '6px 14px', border: '1px solid #fde68a', background: '#fff',
                        color: '#92400e', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      }}>
                      {t('sp_go_to_packages', 'Go to Packages →')}
                    </button>
                  </div>
                </div>
              )}
              <div style={{
                padding: 20, border: '1px dashed var(--card-border)', borderRadius: 8,
                background: '#fff',
                fontFamily: branding.bodyFont ? `'${branding.bodyFont}', sans-serif` : 'inherit',
              }}>
                <PreviewWidget packages={packages} branding={branding} />
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, fontStyle: 'italic' }}>
                {t('sp_layout_h', 'Layout')}: <strong>{branding.layout || 'cards'}</strong> · {t('sp_style_short', 'Style')}: <strong>{branding.style || 'inherit'}</strong> · {t('sp_tweaks_update_instantly', 'Tweaks update instantly. Save to apply.')}
              </div>
            </div>

            {/* ── Save bar ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
              <button type="button" onClick={() => navigate('/portal')} className="btn btn-outline-dark" style={{ padding: '12px 24px' }}>
                {t('cancel', 'Cancel')}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {savedAt && (
                  <span style={{ fontSize: 12, color: '#16a34a' }}>
                    {t('sp_saved_at', 'Saved at')} {savedAt.toLocaleTimeString()}
                  </span>
                )}
                <button type="button" onClick={handleSave} disabled={saving}
                  className="btn btn-primary" style={{ padding: '14px 32px', opacity: saving ? 0.5 : 1 }}>
                  {saving ? t('sp_saving', 'Saving...') : t('sp_save_branding', 'Save branding')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </PortalShell>
  )
}

function Pill({ label, value }) {
  return (
    <span style={{
      padding: '2px 8px', background: '#fff', border: '1px solid #d1d5db',
      borderRadius: 10, fontSize: 11, color: '#374151',
    }}>
      <span style={{ color: '#9ca3af', marginRight: 4 }}>{label}:</span>
      <strong>{value}</strong>
    </span>
  )
}

function ColorPill({ label, hex }) {
  return (
    <span style={{
      padding: '2px 8px', background: '#fff', border: '1px solid #d1d5db',
      borderRadius: 10, fontSize: 11, color: '#374151',
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      <span style={{ width: 10, height: 10, borderRadius: 2, background: hex, border: '1px solid rgba(0,0,0,0.1)' }} />
      <span style={{ color: '#9ca3af' }}>{label}:</span>
      <strong style={{ fontFamily: 'monospace' }}>{hex}</strong>
    </span>
  )
}

// ─── PreviewWidget — renders all packages in the chosen widget layout ───
//
// Mirrors the 5 layouts in Tournament-API/src/views/partials/widget.ejs
// (cards, comparison, hero, minimal, featured) so the merchant can see
// what their actual widget will look like with their fonts and colors.
// Adds a "Powered by InteGolf" footer at the bottom of every layout.
// Small "$X total" hint shown under the per-person price when both are set
// and they differ (i.e., NumGolfers > 1). Used for the reseller flow where
// merchants want to show both the per-person AND bundle totals; for
// single-golfer S&P packages it auto-hides.
// ─── i18n helpers for the preview layouts ───────────────────────────────
// The layout chrome (per-person, metadata, CTAs) renders in the viewer's
// language: the portal preview follows the admin's language; the customer
// embed follows the golfer's EN/FR toggle.
function tNights(t, n)  { return n === 1 ? t('sp_pw_night',  '{n} night',  { n }) : t('sp_pw_nights',  '{n} nights',  { n }) }
function tRounds(t, n)  { return n === 1 ? t('sp_pw_round',  '{n} round',  { n }) : t('sp_pw_rounds',  '{n} rounds',  { n }) }
function tGolfers(t, n) { return n === 1 ? t('sp_pw_golfer', '{n} golfer', { n }) : t('sp_pw_golfers', '{n} golfers', { n }) }
function tCourses(t, n) { return n === 1 ? t('sp_pw_course', '{n} course', { n }) : t('sp_pw_courses', '{n} courses', { n }) }
// "N nights · N rounds · N golfers" — golfers shown by default on every package
// tile (pass golfers:false only where the count truly doesn't apply); upper for
// the caps style. NOTE: bundle layouts don't use this helper (they have no
// golfer count), so the default is safe for them.
function pkgMeta(t, pkg, { golfers = true, upper = false } = {}) {
  const parts = [tNights(t, Number(pkg.Nights) || 1), tRounds(t, Number(pkg.Rounds) || 1)]
  if (golfers) parts.push(tGolfers(t, Number(pkg.NumGolfers || pkg.MaxGolfers || 2) || 2))
  const s = parts.join(' · ')
  return upper ? s.toUpperCase() : s
}

function TotalPriceHint({ pkg, branding, color }) {
  const { t } = useLanguage()
  const total = Number(pkg?.Price) || 0
  const perPerson = Number(pkg?.PricePerPerson) || 0
  if (!total || !perPerson || total <= perPerson) return null
  return (
    <div style={{
      fontSize: 10,
      color: color || '#94a3b8',
      marginTop: 2,
      ...bodyFontStyle(branding),
    }}>
      {t('sp_pw_total', '{money} total', { money: fmtMoney(total) })}
    </div>
  )
}

// "Starting from" eyebrow shown above every package price on every layout —
// prices rise with options (premium tee times, add-ons), so the number is a
// floor, not a fixed total.
function StartingFrom({ style }) {
  const { t } = useLanguage()
  return (
    <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.3, ...style }}>
      {t('sp_pw_starting_from', 'Starting from')}
    </div>
  )
}

// Mobile: collapse the (desktop) multi-column tile grids to a single column so
// package cards + buttons don't clip off the right edge in portrait. Scoped to
// PreviewWidget's own subtree (.sp-pw-root) so it can't touch the tee-time grid
// or anything else, and only fires <=640px — desktop is byte-identical.
const PW_RESPONSIVE_CSS = `@media (max-width:640px){.sp-pw-root [style*="grid-template-columns"]{grid-template-columns:1fr !important}.sp-pw-root [style*="grid-template-columns"]>*{min-width:0 !important}}`

export function PreviewWidget({ packages, branding, onPackageClick, suppressPoweredBy, mode }) {
  const { t } = useLanguage()
  // Golf-bundle groupings (EPIC #116) have a different economic shape —
  // N prepaid rounds for a fixed price, no nights / per-person / hotel.
  // They render through their own card layout below and skip all the
  // Stay & Play normalization + layout switch, so the shared S&P preview
  // is untouched.
  const isBundles = mode === 'bundles'
  // Defensive: coalesce missing numeric fields so a stale backend payload
  // (e.g. PM2 not restarted after a controller change) doesn't render as
  // "$NaN" or the literal string "rounds " (no number) in the layouts.
  // The fields read by every layout are PricePerPerson, Price, Nights,
  // Rounds, NumGolfers — derive any that are missing.
  if (!isBundles && Array.isArray(packages)) {
    packages = packages.map(p => {
      const golfers   = Number(p.NumGolfers || p.MaxGolfers || p.MinGolfers || 1) || 1
      const totalRaw  = p.Price != null ? Number(p.Price) : (p.totalPrice != null ? Number(p.totalPrice) : null)
      const total     = Number.isFinite(totalRaw) ? totalRaw : 0
      const perPerson = Number.isFinite(Number(p.PricePerPerson))
        ? Number(p.PricePerPerson)
        : (golfers ? Math.round((total / golfers) * 100) / 100 : 0)
      return {
        ...p,
        // Descriptions may contain Markdown — flatten to plain text for the
        // card snippets (full Markdown renders on the embed detail/checkout).
        Description:    stripMarkdown(p.Description),
        Nights:         Number(p.Nights || 1) || 1,
        Rounds:         Number(p.Rounds || p.RoundsPerGolfer || 1) || 1,
        NumGolfers:     golfers,
        Price:          total,
        PricePerPerson: perPerson,
      }
    })
  }
  // When onPackageClick is provided (customer iframe), we wrap the
  // layout in a delegating click handler that walks up to find a
  // `[data-pkg-id]` attribute on the closest ancestor and looks the
  // package up by ID. The previewing portal call leaves it undefined,
  // making the layouts read-only as before.
  let layout
  if (isBundles) {
    // Bundles support a focused subset of layouts (cards / comparison /
    // minimal / featured) — no hotel imagery / itinerary / matrix templates.
    const bundleList = ((Array.isArray(packages) && packages.length) ? packages : [BUNDLE_PLACEHOLDER])
      .map(b => ({ ...b, Description: stripMarkdown(b.Description) }))
    switch (branding.layout) {
      case 'comparison': layout = <PreviewBundleComparisonLayout bundles={bundleList} branding={branding} />; break
      case 'minimal':    layout = <PreviewBundleMinimalLayout bundles={bundleList} branding={branding} />; break
      case 'featured':   layout = <PreviewBundleFeaturedLayout bundles={bundleList} branding={branding} />; break
      case 'cards':
      default:           layout = <PreviewBundleCardsLayout bundles={bundleList} branding={branding} />; break
    }
  } else if (!packages || packages.length === 0) {
    layout = <PreviewCardLayout packages={[PLACEHOLDER_PKG]} branding={branding} />
  } else {
    switch (branding.layout || 'cards') {
      case 'comparison': layout = <PreviewComparisonLayout packages={packages} branding={branding} />; break
      case 'hero':       layout = <PreviewHeroLayout packages={packages} branding={branding} />; break
      case 'minimal':    layout = <PreviewMinimalLayout packages={packages} branding={branding} />; break
      case 'featured':   layout = <PreviewFeaturedLayout packages={packages} branding={branding} />; break
      case 'itinerary':  layout = <PreviewItineraryLayout packages={packages} branding={branding} />; break
      case 'gallery':    layout = <PreviewGalleryLayout packages={packages} branding={branding} />; break
      case 'carousel':   layout = <PreviewCarouselLayout packages={packages} branding={branding} />; break
      case 'matrix':     layout = <PreviewMatrixLayout packages={packages} branding={branding} />; break
      case 'split':      layout = <PreviewSplitLayout packages={packages} branding={branding} />; break
      case 'cards':
      default:           layout = <PreviewCardLayout packages={packages} branding={branding} />; break
    }
  }
  if (onPackageClick) {
    const handleClick = (e) => {
      const node = e.target.closest('[data-pkg-id]')
      if (!node) return
      const id = Number(node.getAttribute('data-pkg-id'))
      const pkg = packages.find(p => Number(p.ID_NO) === id)
      if (pkg) onPackageClick(pkg)
    }
    return (
      <div className="sp-pw-root" style={{ display: 'contents' }}>
        <style>{PW_RESPONSIVE_CSS}</style>
        <div onClick={handleClick} style={{ cursor: 'pointer' }}>{layout}</div>
        {!suppressPoweredBy && <PoweredByInteGolf branding={branding} />}
      </div>
    )
  }
  return (
    <div className="sp-pw-root" style={{ display: 'contents' }}>
      <style>{PW_RESPONSIVE_CSS}</style>
      {layout}
      {!suppressPoweredBy && <PoweredByInteGolf branding={branding} />}
    </div>
  )
}

// ─── Powered-by-InteGolf footer — shown on every layout ───
function PoweredByInteGolf({ branding }) {
  return (
    <div style={{
      marginTop: 16,
      paddingTop: 14,
      borderTop: '1px solid #e5e7eb',
      textAlign: 'center',
      ...bodyFontStyle(branding),
    }}>
      <a
        href="https://integolf.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: 11,
          color: '#64748b',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          letterSpacing: 0.3,
        }}
      >
        Powered by
        <strong style={{ color: '#1e293b', marginLeft: 4 }}>InteGolf</strong>
        <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 2 }}>↗</span>
      </a>
    </div>
  )
}

// Format a price with commas — used by every layout below.
// "1234.5" → "$1,234.50". Pass {whole:true} to drop cents.
function fmtMoney(value, { whole } = {}) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '$0'
  const opts = whole
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  return '$' + n.toLocaleString('en-CA', opts)
}

const PLACEHOLDER_PKG = {
  ID_NO: 0,
  PackageName: 'Weekend Getaway',
  Description: 'Sample placeholder — create real packages to preview them here.',
  PricePerPerson: 299,
  Nights: 2, Rounds: 2, NumGolfers: 2,
  inclusions: [{ ItemName: 'One round of golf' }, { ItemName: '1 night accommodation' }, { ItemName: 'Cart included' }],
}

// Golf-bundle sample (EPIC #116) — a prepaid-round pass, no hotel.
const BUNDLE_PLACEHOLDER = {
  ID_NO: 0,
  Name: '4-Round Pass',
  Description: 'Sample placeholder — create real bundles to preview them here.',
  RoundsCount: 4,
  Price: 325,
  Currency: 'CAD',
  DaysAllowed: '1111100',
  CourseCount: 1,
}

// Resolved style helpers (used by every layout)
function headerFontStyle(branding) {
  return {
    fontFamily: branding.headerFont ? `'${branding.headerFont}', serif` : 'inherit',
    fontWeight: branding.headerWeight || 700,
    textTransform: branding.headerCase || 'none',
    letterSpacing: branding.headerLetterSpacing || 'normal',
  }
}
function bodyFontStyle(branding) {
  return {
    fontFamily: branding.bodyFont ? `'${branding.bodyFont}', sans-serif` : 'inherit',
  }
}

/**
 * Resolve the merchant's brand colors with fallbacks. Separates PRIMARY
 * (the dominant brand color — used for titles and brand anchors) from
 * ACCENT (the CTA color — used for buttons, prices, highlights). Both
 * come from the AI-extracted values when the merchant used "Match my
 * website", or from manual entry in custom mode.
 *
 * When the style is 'inherit' and no color is set, we fall back to the
 * default green so the preview is never blank — but if a color IS set
 * (e.g. after an AI extract), we use it regardless of style so the
 * merchant can see their extraction actually applied.
 */
function resolveColors(branding) {
  const primary = branding.primaryColor || '#1B5E20'      // brand anchor color
  const accent = branding.accentColor || primary           // CTAs fall back to primary if no accent
  const secondary = branding.secondaryColor || '#f8fafc'   // subtle backgrounds
  return { primary, accent, secondary }
}

/**
 * Resolve the hero image for a package. Priority:
 * 1. branding.packageImages[pkg.ID_NO] (uploaded via the Branding page)
 * 2. pkg.HeroImageUrl (set on the package itself)
 * 3. null (the layout renders a gradient fallback)
 *
 * Legacy data may have relative URLs like "/site-assets/uploads/X.jpg".
 * In a customer-embedded context the iframe runs on a different origin
 * from the API, so a bare-relative URL resolves to the iframe host and
 * silently 404s. Normalize here by prepending the API origin (same
 * pattern resolveUrl() uses in ResellerEmbed.jsx) — older URLs render
 * correctly without needing a DB backfill.
 */
function resolvePackageImage(pkg, branding) {
  const raw = (branding.packageImages || {})[pkg.ID_NO] || pkg.HeroImageUrl || null
  if (!raw) return null
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw
  const apiOrigin = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_ORIGIN)
    || (typeof window !== 'undefined' ? window.location.origin : '')
  return apiOrigin + raw
}

function buttonStyle(branding, size = 'md') {
  const { accent } = resolveColors(branding)
  const padding = size === 'sm' ? '8px 18px' : size === 'lg' ? '14px 28px' : '11px 22px'
  return {
    display: 'inline-block',
    padding,
    background: accent,
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontWeight: 700,
    fontSize: size === 'sm' ? 12 : 14,
    textDecoration: 'none',
    cursor: 'default',
    fontFamily: branding.bodyFont ? `'${branding.bodyFont}', sans-serif` : 'inherit',
  }
}
function inclusionPillStyle(branding) {
  const { primary } = resolveColors(branding)
  // Pills use the primary brand color so they tie to the course identity
  // rather than the CTA accent. Subtle 15% background, 55% border alpha.
  return {
    fontSize: 11,
    padding: '3px 10px',
    background: primary + '15',
    color: primary,
    border: '1px solid ' + primary + '55',
    borderRadius: 12,
  }
}

// ─── Layout 1: cards (default) — equal-size grid ───
function PreviewCardLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    // auto-fit (not auto-fill) collapses empty tracks so the card row
    // centers with `justify-content` instead of left-aligning. Cap card
    // width at 360px so a single-package preview doesn't stretch the
    // card to the full container width and look weird.
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 360px))', gap: 16, justifyContent: 'center' }}>
      {packages.map(pkg => (
        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
          background: '#fff', borderRadius: 10,
          boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
          // Subtle primary-colored top border anchors the card to the brand
          borderTop: '3px solid ' + primary,
          border: '1px solid #f1f5f9',
          padding: 18, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <h4 style={{ ...headerFontStyle(branding), margin: 0, fontSize: 17, color: primary }}>{pkg.PackageName}</h4>
            <div style={{ textAlign: 'right' }}>
              <StartingFrom />
              <div style={{ fontSize: 22, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(pkg.PricePerPerson, { whole: true })}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{t('sp_pw_per_person', 'per person')}</div>
              <TotalPriceHint pkg={pkg} branding={branding} />
            </div>
          </div>
          <div style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#666', marginBottom: 10 }}>
            {pkgMeta(t, pkg, { golfers: true })}
          </div>
          {pkg.Description && (
            <p style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#475569', margin: '0 0 10px', lineHeight: 1.4, flex: 1 }}>
              {pkg.Description.length > 120 ? pkg.Description.slice(0, 120) + '...' : pkg.Description}
            </p>
          )}
          {pkg.inclusions?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {pkg.inclusions.slice(0, 4).map((inc, i) => (
                <span key={i} style={inclusionPillStyle(branding)}>✓ {inc.ItemName}</span>
              ))}
              {pkg.inclusions.length > 4 && (
                <span style={{ fontSize: 11, color: '#888', padding: '3px 6px' }}>{t('sp_pw_more', '+{n} more', { n: pkg.inclusions.length - 4 })}</span>
              )}
            </div>
          )}
          <div style={{ marginTop: 'auto' }}>
            <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_select_package', 'Select Package')}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Golf-bundle helpers (EPIC #116) ───
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
// '1111100' (Mon..Sun mask) -> a compact human label, or null when the
// bundle is playable any day (no restriction chip needed).
const DAY_KEYS_PW = ['sp_day_mon', 'sp_day_tue', 'sp_day_wed', 'sp_day_thu', 'sp_day_fri', 'sp_day_sat', 'sp_day_sun']
function formatBundleDays(t, mask) {
  const s = String(mask || '1111111')
  if (!/^[01]{7}$/.test(s) || s === '1111111') return null
  if (s === '1111100') return `${t('sp_day_mon', 'Mon')}–${t('sp_day_fri', 'Fri')}`
  if (s === '0000011') return t('sp_pw_weekends', 'Weekends')
  const on = []
  for (let i = 0; i < 7; i++) if (s[i] === '1') on.push(t(DAY_KEYS_PW[i], DAY_ABBR[i]))
  return on.length ? on.join(', ') : null
}
function fmtTime12(v) {
  if (!v) return null
  const m = String(v).match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  let h = Number(m[1]); const min = m[2]
  const ap = h >= 12 ? 'PM' : 'AM'
  h = h % 12; if (h === 0) h = 12
  return min === '00' ? `${h} ${ap}` : `${h}:${min} ${ap}`
}
function formatBundleTimeWindow(t, start, end) {
  const s = fmtTime12(start), e = fmtTime12(end)
  if (s && e) return `${s}–${e}`
  if (s) return t('sp_pw_after', 'After {time}', { time: s })
  if (e) return t('sp_pw_before', 'Before {time}', { time: e })
  return null
}
function formatBundleExpiry(d) {
  if (!d) return null
  const dt = new Date(String(d).slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(dt.getTime())) return null
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}
// Derive every display value a bundle layout needs from one row, so the
// four bundle layouts stay consistent.
function bundleMeta(t, bnd) {
  const rounds = Math.max(1, Number(bnd.RoundsCount) || 1)
  const price = Number(bnd.Price) || 0
  return {
    rounds,
    price,
    perRound: rounds ? price / rounds : 0,
    days: formatBundleDays(t, bnd.DaysAllowed),
    timeWin: formatBundleTimeWindow(t, bnd.TimeWindowStart, bnd.TimeWindowEnd),
    expiry: formatBundleExpiry(bnd.ExpiryDate),
    courseCount: Number(bnd.CourseCount) || (Array.isArray(bnd.courses) ? bnd.courses.length : 0),
  }
}
// The chip set shared by the bundle layouts (rounds + any restrictions).
function bundleChips(t, m) {
  const chips = [m.rounds === 1
    ? t('sp_pw_prepaid_round', '{n} prepaid round', { n: m.rounds })
    : t('sp_pw_prepaid_rounds', '{n} prepaid rounds', { n: m.rounds })]
  if (m.days) chips.push(m.days)
  if (m.timeWin) chips.push(m.timeWin)
  if (m.expiry) chips.push(t('sp_pw_valid_until', 'Valid until {d}', { d: m.expiry }))
  return chips
}

// ─── Bundle layout: prepaid-round passes (golf bundles, EPIC #116) ───
// Mirrors the S&P card grid visually (same brand colours / fonts) but the
// content is golf-only: rounds count + prepaid total, no nights / rooms /
// per-person / cart.
function PreviewBundleCardsLayout({ bundles, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 360px))', gap: 16, justifyContent: 'center' }}>
      {bundles.map(bnd => {
        const m = bundleMeta(t, bnd)
        return (
          <div key={bnd.ID_NO} data-pkg-id={bnd.ID_NO} style={{
            background: '#fff', borderRadius: 10,
            boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
            borderTop: '3px solid ' + primary,
            border: '1px solid #f1f5f9',
            padding: 18, display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h4 style={{ ...headerFontStyle(branding), margin: 0, fontSize: 17, color: primary }}>{bnd.Name}</h4>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(m.price, { whole: true })}</div>
                <div style={{ fontSize: 10, color: '#888' }}>{t('sp_pw_prepaid', 'prepaid')}</div>
              </div>
            </div>
            <div style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#666', marginBottom: 10 }}>
              {tRounds(t, m.rounds)}
              {m.perRound > 0 && <> · {fmtMoney(m.perRound, { whole: true })}{t('sp_pw_round_suffix', '/round')}</>}
              {m.courseCount > 0 && <> · {tCourses(t, m.courseCount)}</>}
            </div>
            {bnd.Description && (
              <p style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#475569', margin: '0 0 10px', lineHeight: 1.4, flex: 1 }}>
                {bnd.Description.length > 120 ? bnd.Description.slice(0, 120) + '...' : bnd.Description}
              </p>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {bundleChips(t, m).map((c, i) => <span key={i} style={inclusionPillStyle(branding)}>{i === 0 ? '✓ ' : ''}{c}</span>)}
            </div>
            <div style={{ marginTop: 'auto' }}>
              <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_buy_bundle', 'Buy bundle')}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Bundle layout: comparison — side-by-side equal columns ───
function PreviewBundleComparisonLayout({ bundles, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  const middleIdx = Math.floor(bundles.length / 2)
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${bundles.length}, 1fr)`,
      gap: 0,
      border: '1px solid ' + primary + '33',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {bundles.map((bnd, idx) => {
        const m = bundleMeta(t, bnd)
        return (
          <div key={bnd.ID_NO} data-pkg-id={bnd.ID_NO} style={{
            padding: 20,
            textAlign: 'center',
            background: idx === middleIdx ? primary + '08' : '#fff',
            borderRight: idx < bundles.length - 1 ? '1px solid ' + primary + '22' : 'none',
            borderTop: idx === middleIdx ? '3px solid ' + primary : 'none',
            display: 'flex', flexDirection: 'column',
          }}>
            <h4 style={{ ...headerFontStyle(branding), margin: '0 0 6px', fontSize: 17, color: primary }}>{bnd.Name}</h4>
            <div style={{ fontSize: 28, fontWeight: 800, color: accent, margin: '8px 0' }}>{fmtMoney(m.price, { whole: true })}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{t('sp_pw_prepaid', 'prepaid')}</div>
            <div style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#555', margin: '10px 0 12px' }}>
              {tRounds(t, m.rounds)}
              {m.perRound > 0 && <> · {fmtMoney(m.perRound, { whole: true })}{t('sp_pw_round_suffix', '/round')}</>}
            </div>
            <div style={{ ...bodyFontStyle(branding), textAlign: 'left', flex: 1, marginBottom: 12 }}>
              {bundleChips(t, m).map((c, i) => (
                <div key={i} style={{ fontSize: 11, padding: '3px 0', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: primary, fontWeight: 700, marginRight: 4 }}>✓</span> {c}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 'auto' }}>
              <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_buy_bundle', 'Buy bundle')}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Bundle layout: minimal — clean list with accent left border ───
function PreviewBundleMinimalLayout({ bundles, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      {bundles.map((bnd, idx) => {
        const m = bundleMeta(t, bnd)
        return (
          <div key={bnd.ID_NO} data-pkg-id={bnd.ID_NO} style={{
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '18px 20px',
            borderBottom: idx < bundles.length - 1 ? '1px solid #e5e7eb' : 'none',
            borderLeft: '4px solid ' + primary,
            background: '#fff',
          }}>
            <div style={{ flex: 1 }}>
              <h4 style={{ ...headerFontStyle(branding), margin: '0 0 4px', fontSize: 16, color: primary }}>{bnd.Name}</h4>
              <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#888' }}>
                {tRounds(t, m.rounds)}
                {m.days && ` · ${m.days}`}
                {m.timeWin && ` · ${m.timeWin}`}
                {bnd.Description && ` — ${bnd.Description.slice(0, 60)}${bnd.Description.length > 60 ? '...' : ''}`}
              </div>
            </div>
            <div style={{ textAlign: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(m.price, { whole: true })}</div>
              <div style={{ fontSize: 10, color: '#888' }}>{t('sp_pw_prepaid', 'prepaid')}</div>
            </div>
            <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_buy_bundle', 'Buy bundle')}</span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Bundle layout: featured — hero on top, supporting bundles below ───
function PreviewBundleFeaturedLayout({ bundles, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  const feat = bundles[0]
  const others = bundles.slice(1)
  const fm = bundleMeta(t, feat)
  const heroImage = branding.featuredImageUrl || feat.HeroImageUrl
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div data-pkg-id={feat.ID_NO} style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        background: '#fff',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 8px 24px ' + primary + '22',
        border: '1px solid ' + primary + '22',
        minHeight: 240,
      }}>
        {/* Hero image / gradient side */}
        <div style={{
          background: heroImage
            ? `url('${heroImage}') center/cover no-repeat`
            : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
          minHeight: 240,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          padding: 20,
        }}>
          {!heroImage && (
            <span style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 64, opacity: 0.35,
            }}>⛳</span>
          )}
          <div style={{
            position: 'relative', zIndex: 1,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 20,
            fontSize: 10, fontWeight: 800, letterSpacing: 1,
            color: primary,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <span style={{ fontSize: 10 }} aria-hidden="true">★</span>
            {t('sp_pw_featured', 'FEATURED')}
          </div>
        </div>

        {/* Content side */}
        <div style={{ padding: 28, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ ...headerFontStyle(branding), margin: '0 0 4px', fontSize: 24, color: primary, lineHeight: 1.15 }}>
            {feat.Name}
          </h3>
          <div style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#888', marginBottom: 12, letterSpacing: 0.3 }}>
            {tRounds(t, fm.rounds).toUpperCase()}
            {fm.courseCount > 0 && <> · {tCourses(t, fm.courseCount).toUpperCase()}</>}
            {fm.days && ` · ${fm.days.toUpperCase()}`}
          </div>
          {feat.Description && (
            <p style={{ ...bodyFontStyle(branding), fontSize: 13, color: '#475569', margin: '0 0 14px', lineHeight: 1.5 }}>
              {feat.Description.length > 160 ? feat.Description.slice(0, 160) + '...' : feat.Description}
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
            {bundleChips(t, fm).map((c, i) => <span key={i} style={inclusionPillStyle(branding)}>✓ {c}</span>)}
          </div>
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{t('sp_pw_prepaid_cap', 'Prepaid')}</div>
              <div>
                <span style={{ fontSize: 34, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(fm.price, { whole: true })}</span>
                {fm.perRound > 0 && <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>· {fmtMoney(fm.perRound, { whole: true })}{t('sp_pw_round_suffix', '/round')}</span>}
              </div>
            </div>
            <span style={{ ...buttonStyle(branding, 'lg'), padding: '14px 28px', fontSize: 14, boxShadow: '0 4px 12px ' + accent + '44' }}>{t('sp_pw_buy_bundle', 'Buy bundle')}</span>
          </div>
        </div>
      </div>

      {/* Other bundles — header + grid of smaller cards */}
      {others.length > 0 && (
        <div>
          <div style={{
            ...headerFontStyle(branding),
            fontSize: 13, fontWeight: 700, color: primary,
            textTransform: 'uppercase', letterSpacing: 1.5,
            marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span>{t('sp_pw_more_bundles', 'More Bundles')}</span>
            <div style={{ flex: 1, height: 1, background: primary + '33' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(others.length, 3)}, 1fr)`, gap: 14 }}>
            {others.map(bnd => {
              const m = bundleMeta(t, bnd)
              return (
                <div key={bnd.ID_NO} data-pkg-id={bnd.ID_NO} style={{
                  background: '#fff', borderRadius: 10,
                  boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
                  border: '1px solid ' + primary + '22',
                  borderTop: '3px solid ' + primary,
                  padding: 16, display: 'flex', flexDirection: 'column',
                }}>
                  <h5 style={{ ...headerFontStyle(branding), margin: '0 0 4px', fontSize: 15, color: primary }}>{bnd.Name}</h5>
                  <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#888', marginBottom: 10 }}>
                    {tRounds(t, m.rounds)}
                    {m.days && ` · ${m.days}`}
                  </div>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginTop: 'auto', paddingTop: 10, borderTop: '1px solid #f1f5f9',
                  }}>
                    <div>
                      <span style={{ fontSize: 20, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(m.price, { whole: true })}</span>
                      <span style={{ fontSize: 10, color: '#888', marginLeft: 3 }}>{t('sp_pw_prepaid', 'prepaid')}</span>
                    </div>
                    <span style={{ ...buttonStyle(branding, 'sm'), padding: '6px 14px', fontSize: 11 }}>{t('sp_pw_buy', 'Buy')}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Layout 2: comparison — side-by-side equal columns ───
function PreviewComparisonLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  const middleIdx = Math.floor(packages.length / 2)
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${packages.length}, 1fr)`,
      gap: 0,
      border: '1px solid ' + primary + '33',
      borderRadius: 10,
      overflow: 'hidden',
    }}>
      {packages.map((pkg, idx) => (
        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
          padding: 20,
          textAlign: 'center',
          background: idx === middleIdx ? primary + '08' : '#fff',
          borderRight: idx < packages.length - 1 ? '1px solid ' + primary + '22' : 'none',
          borderTop: idx === middleIdx ? '3px solid ' + primary : 'none',
          display: 'flex', flexDirection: 'column',
        }}>
          <h4 style={{ ...headerFontStyle(branding), margin: '0 0 6px', fontSize: 17, color: primary }}>{pkg.PackageName}</h4>
          <StartingFrom style={{ marginTop: 8 }} />
          <div style={{ fontSize: 28, fontWeight: 800, color: accent, margin: '2px 0 0' }}>{fmtMoney(pkg.PricePerPerson, { whole: true })}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{t('sp_pw_per_person', 'per person')}</div>
          <TotalPriceHint pkg={pkg} branding={branding} />
          <div style={{ marginBottom: 12 }} />
          <div style={{ ...bodyFontStyle(branding), fontSize: 12, color: '#555', marginBottom: 12 }}>
            {pkgMeta(t, pkg)}
          </div>
          {pkg.inclusions?.length > 0 && (
            <div style={{ ...bodyFontStyle(branding), textAlign: 'left', flex: 1, marginBottom: 12 }}>
              {pkg.inclusions.slice(0, 5).map((inc, i) => (
                <div key={i} style={{ fontSize: 11, padding: '3px 0', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ color: primary, fontWeight: 700, marginRight: 4 }}>✓</span> {inc.ItemName}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 'auto' }}>
            <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_book_now', 'Book Now')}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Layout 3: hero — white card with primary border + big side price ───
function PreviewHeroLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {packages.map(pkg => {
        // Mirror the embed's image fallback chain so the preview never
        // diverges from what visitors actually see.
        const img = resolvePackageImage(pkg, branding)
        return (
        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
          display: 'grid',
          gridTemplateColumns: img ? '160px 1fr 220px' : '1fr 220px',
          background: '#fff',
          border: '4px solid ' + primary,
          borderRadius: 12,
          overflow: 'hidden',
          minHeight: 160,
          boxShadow: '0 2px 12px ' + primary + '22',
        }}>
          {/* Optional hero image on the far left. Needs an explicit minHeight:
              on desktop the grid stretches it to the row, but on mobile the
              columns collapse to one, and without a height this empty div would
              render at 0px (image invisible). */}
          {img && (
            <div style={{
              background: `url('${img}') center/cover no-repeat`,
              borderRight: '1px solid ' + primary + '22',
              minHeight: 160,
            }} />
          )}

          {/* Middle: white content area with dark text */}
          <div style={{
            padding: '22px 28px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <h4 style={{
              ...headerFontStyle(branding),
              margin: '0 0 4px',
              fontSize: 22,
              color: primary,
              lineHeight: 1.15,
            }}>
              {pkg.PackageName}
            </h4>
            <div style={{
              ...bodyFontStyle(branding),
              fontSize: 11,
              color: '#64748b',
              marginBottom: 10,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
            }}>
              {pkgMeta(t, pkg, { golfers: true, upper: true })}
            </div>
            {pkg.Description && (
              <p style={{
                ...bodyFontStyle(branding),
                fontSize: 13,
                color: '#475569',
                margin: '0 0 12px',
                lineHeight: 1.5,
              }}>
                {pkg.Description.length > 140 ? pkg.Description.slice(0, 140) + '...' : pkg.Description}
              </p>
            )}
            {pkg.inclusions?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {pkg.inclusions.slice(0, 5).map((inc, i) => (
                  <span key={i} style={inclusionPillStyle(branding)}>✓ {inc.ItemName}</span>
                ))}
                {pkg.inclusions.length > 5 && (
                  <span style={{ fontSize: 11, color: '#888', padding: '3px 6px' }}>{t('sp_pw_more', '+{n} more', { n: pkg.inclusions.length - 5 })}</span>
                )}
              </div>
            )}
          </div>

          {/* Right: big price + Book Now button in a softly-tinted panel */}
          <div style={{
            background: primary + '08',
            borderLeft: '1px solid ' + primary + '33',
            padding: '22px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}>
            <div style={{
              ...bodyFontStyle(branding),
              fontSize: 10,
              color: primary,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              marginBottom: 4,
              opacity: 0.7,
            }}>
              {t('sp_pw_starting_from', 'Starting from')}
            </div>
            <div style={{
              fontSize: 42,
              fontWeight: 800,
              color: accent,
              lineHeight: 1,
              marginBottom: 2,
            }}>
              {fmtMoney(pkg.PricePerPerson, { whole: true })}
            </div>
            <div style={{
              ...bodyFontStyle(branding),
              fontSize: 11,
              color: '#64748b',
              marginBottom: 4,
            }}>
              {t('sp_pw_per_person', 'per person')}
            </div>
            <div style={{ marginBottom: 14 }}><TotalPriceHint pkg={pkg} branding={branding} /></div>
            <span style={{
              ...buttonStyle(branding, 'md'),
              display: 'block',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 2px 8px ' + accent + '44',
            }}>
              {t('sp_pw_book_now', 'Book Now')}
            </span>
          </div>
        </div>
        )
      })}
    </div>
  )
}

// ─── Layout 4: minimal — clean rows with primary-colored border ───
function PreviewMinimalLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      {packages.map((pkg, idx) => (
        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '18px 20px',
          borderBottom: idx < packages.length - 1 ? '1px solid #e5e7eb' : 'none',
          borderLeft: '4px solid ' + primary,
          background: '#fff',
        }}>
          <div style={{ flex: 1 }}>
            <h4 style={{ ...headerFontStyle(branding), margin: '0 0 4px', fontSize: 16, color: primary }}>{pkg.PackageName}</h4>
            <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#888' }}>
              {pkgMeta(t, pkg)}
              {pkg.Description && ` — ${pkg.Description.slice(0, 60)}${pkg.Description.length > 60 ? '...' : ''}`}
            </div>
          </div>
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <StartingFrom />
            <div style={{ fontSize: 22, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(pkg.PricePerPerson, { whole: true })}</div>
            <div style={{ fontSize: 10, color: '#888' }}>{t('sp_pw_per_person', 'per person')}</div>
            <TotalPriceHint pkg={pkg} branding={branding} />
          </div>
          <span style={buttonStyle(branding, 'sm')}>{t('sp_pw_book_now', 'Book Now')}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Layout 5: featured — hero on top, supporting packages as cards below ───
function PreviewFeaturedLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  // The hero is the package flagged IsFeatured; if none is flagged, fall back
  // to the first package. The rest become "More options" in their given order.
  const featIdx = Math.max(0, packages.findIndex(p => p.IsFeatured))
  const featPkg = packages[featIdx]
  const otherPkgs = packages.filter((_, i) => i !== featIdx)
  // Branding-level featured image wins; falls back to the package's own
  // HeroImageUrl; falls back to a brand-colored gradient
  const heroImage = branding.featuredImageUrl || featPkg.HeroImageUrl
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Hero featured package — data-pkg-id makes it clickable in the
          customer iframe via PreviewWidget's delegated handler */}
      <div data-pkg-id={featPkg.ID_NO} style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        background: '#fff',
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: '0 8px 24px ' + primary + '22',
        border: '1px solid ' + primary + '22',
        minHeight: 240,
      }}>
        {/* Hero image / gradient side */}
        <div style={{
          background: heroImage
            ? `url('${heroImage}') center/cover no-repeat`
            : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
          minHeight: 240,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          padding: 20,
        }}>
          {!heroImage && (
            <span style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: 64, opacity: 0.35,
            }}>⛳</span>
          )}
          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: 20,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1,
            color: primary,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}>
            <span style={{ fontSize: 10 }} aria-hidden="true">★</span>
            {t('sp_pw_featured', 'FEATURED')}
          </div>
        </div>

        {/* Content side */}
        <div style={{ padding: 28, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{
            ...headerFontStyle(branding),
            margin: '0 0 4px',
            fontSize: 24,
            color: primary,
            lineHeight: 1.15,
          }}>
            {featPkg.PackageName}
          </h3>
          <div style={{
            ...bodyFontStyle(branding),
            fontSize: 12,
            color: '#888',
            marginBottom: 12,
            letterSpacing: 0.3,
          }}>
            {pkgMeta(t, featPkg, { golfers: true, upper: true })}
          </div>
          {featPkg.Description && (
            <p style={{
              ...bodyFontStyle(branding),
              fontSize: 13,
              color: '#475569',
              margin: '0 0 14px',
              lineHeight: 1.5,
            }}>
              {featPkg.Description.length > 160 ? featPkg.Description.slice(0, 160) + '...' : featPkg.Description}
            </p>
          )}
          {featPkg.inclusions?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
              {featPkg.inclusions.slice(0, 5).map((inc, i) => (
                <span key={i} style={inclusionPillStyle(branding)}>✓ {inc.ItemName}</span>
              ))}
              {featPkg.inclusions.length > 5 && (
                <span style={{ fontSize: 11, color: '#888', padding: '3px 6px' }}>{t('sp_pw_more', '+{n} more', { n: featPkg.inclusions.length - 5 })}</span>
              )}
            </div>
          )}
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{t('sp_pw_starting_from', 'Starting from')}</div>
              <div>
                <span style={{ fontSize: 34, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(featPkg.PricePerPerson, { whole: true })}</span>
                <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>{t('sp_pw_per_person_slash', '/ person')}</span>
              </div>
            </div>
            <span style={{
              ...buttonStyle(branding, 'lg'),
              padding: '14px 28px',
              fontSize: 14,
              boxShadow: '0 4px 12px ' + accent + '44',
            }}>{t('sp_pw_book_now', 'Book Now')}</span>
          </div>
        </div>
      </div>

      {/* Other packages — rendered as a header + grid of equal cards */}
      {otherPkgs.length > 0 && (
        <div>
          <div style={{
            ...headerFontStyle(branding),
            fontSize: 13,
            fontWeight: 700,
            color: primary,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span>{t('sp_pw_more_options', 'More Options')}</span>
            <div style={{ flex: 1, height: 1, background: primary + '33' }} />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(otherPkgs.length, 3)}, 1fr)`,
            gap: 14,
          }}>
            {otherPkgs.map(pkg => (
              <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
                background: '#fff',
                borderRadius: 10,
                boxShadow: '0 1px 8px rgba(0,0,0,0.05)',
                border: '1px solid ' + primary + '22',
                borderTop: '3px solid ' + primary,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <h5 style={{
                  ...headerFontStyle(branding),
                  margin: '0 0 4px',
                  fontSize: 15,
                  color: primary,
                }}>{pkg.PackageName}</h5>
                <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#888', marginBottom: 10 }}>
                  {pkgMeta(t, pkg)}
                </div>
                {pkg.inclusions?.length > 0 && (
                  <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#64748b', marginBottom: 12, flex: 1 }}>
                    {pkg.inclusions.slice(0, 3).map((inc, i) => (
                      <div key={i} style={{ padding: '2px 0' }}>
                        <span style={{ color: accent, fontWeight: 700, marginRight: 4 }}>✓</span>
                        {inc.ItemName}
                      </div>
                    ))}
                    {pkg.inclusions.length > 3 && (
                      <div style={{ padding: '2px 0', color: '#9ca3af', fontStyle: 'italic' }}>
                        + {pkg.inclusions.length - 3} more
                      </div>
                    )}
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 'auto',
                  paddingTop: 10,
                  borderTop: '1px solid #f1f5f9',
                }}>
                  <div>
                    <StartingFrom />
                    <span style={{ fontSize: 20, fontWeight: 800, color: accent, lineHeight: 1 }}>{fmtMoney(pkg.PricePerPerson, { whole: true })}</span>
                    <span style={{ fontSize: 10, color: '#888', marginLeft: 3 }}>{t('sp_pw_per_person_slash', '/ person')}</span>
                  </div>
                  <span style={{ ...buttonStyle(branding, 'sm'), padding: '6px 14px', fontSize: 11 }}>{t('sp_pw_book_now', 'Book Now')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Layout 6: itinerary — day-by-day breakdown per package ───
//
// Generates a day timeline from the package's Nights + Rounds. This is
// the killer feature for multi-night Stay & Play packages because it
// answers "what am I actually doing each day of my trip?"
//
// Distribution logic:
//   - Day 1: check in
//   - Rounds are distributed across the days (starting day 2 if there's
//     a "check-in day" gap, otherwise from day 1)
//   - Last day: check out
//
// For single-night packages it collapses to a simple 2-day view.
function buildItinerary(t, pkg) {
  const nights = Math.max(1, Number(pkg.Nights) || 1)
  const rounds = Math.max(1, Number(pkg.Rounds) || 1)
  const totalDays = nights + 1 // nights + 1 arrival/departure day
  const items = []
  // Check in on day 1
  items.push({ day: 1, icon: '🏨', label: t('sp_pw_check_in', 'Check in'), kind: 'arrive' })
  // Distribute rounds across the trip. Prefer days 1..nights (avoid the
  // departure day which is usually just checkout). If rounds > nights,
  // stack multiple rounds on the same day.
  const golfDays = Math.min(nights, totalDays - 1)
  for (let r = 0; r < rounds; r++) {
    const day = Math.min(totalDays - 1, 1 + (r % golfDays))
    items.push({ day, icon: '⛳', label: t('sp_pw_round_n', 'Round {n}', { n: r + 1 }), kind: 'golf' })
  }
  // Check out on the last day
  items.push({ day: totalDays, icon: '👋', label: t('sp_pw_check_out', 'Check out'), kind: 'depart' })
  // Sort by day, then by kind weight so arrive/golf/depart render in order
  const kindWeight = { arrive: 0, golf: 1, depart: 2 }
  items.sort((a, b) => a.day - b.day || kindWeight[a.kind] - kindWeight[b.kind])
  return items
}

function PreviewItineraryLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {packages.map(pkg => {
        const items = buildItinerary(t, pkg)
        return (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
            display: 'grid',
            gridTemplateColumns: '1fr 200px',
            background: '#fff',
            border: '1px solid ' + primary + '33',
            borderRadius: 12,
            overflow: 'hidden',
          }}>
            {/* Left: header + day-by-day timeline */}
            <div style={{ padding: '20px 24px' }}>
              <h4 style={{
                ...headerFontStyle(branding),
                margin: '0 0 4px',
                fontSize: 18,
                color: primary,
              }}>{pkg.PackageName}</h4>
              <div style={{
                ...bodyFontStyle(branding),
                fontSize: 11, color: '#64748b',
                marginBottom: 14,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
              }}>
                {pkgMeta(t, pkg, { golfers: true })}
              </div>

              {/* Timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
                {items.map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '6px 0',
                    position: 'relative',
                  }}>
                    {/* Vertical line connecting the dots (except on last item) */}
                    {i < items.length - 1 && (
                      <div style={{
                        position: 'absolute',
                        left: 14, top: 22, bottom: -4,
                        width: 2, background: primary + '33',
                      }} />
                    )}
                    <div style={{
                      width: 30, height: 30,
                      borderRadius: '50%',
                      background: '#fff',
                      border: '2px solid ' + primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, flexShrink: 0,
                      position: 'relative', zIndex: 1,
                    }}>
                      {item.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        ...bodyFontStyle(branding),
                        fontSize: 10, color: primary,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                        Day {item.day}
                      </div>
                      <div style={{
                        ...bodyFontStyle(branding),
                        fontSize: 13, color: '#1e293b',
                      }}>{item.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: price + button */}
            <div style={{
              background: primary + '08',
              borderLeft: '1px solid ' + primary + '33',
              padding: '20px 16px',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            }}>
              <div style={{
                ...bodyFontStyle(branding),
                fontSize: 10, color: primary, fontWeight: 700,
                letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4, opacity: 0.7,
              }}>{t('sp_pw_starting_from', 'Starting from')}</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: accent, lineHeight: 1 }}>
                {fmtMoney(pkg.PricePerPerson, { whole: true })}
              </div>
              <div style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#64748b' }}>
                {t('sp_pw_per_person', 'per person')}
              </div>
              <div style={{ marginBottom: 12 }}><TotalPriceHint pkg={pkg} branding={branding} /></div>
              <span style={{
                ...buttonStyle(branding, 'md'),
                display: 'block', width: '100%', textAlign: 'center',
              }}>{t('sp_pw_book_trip', 'Book Trip')}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Layout 7: gallery — image-first grid with minimal info bar ───
function PreviewGalleryLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
      {packages.map(pkg => (
        <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
          borderRadius: 12,
          overflow: 'hidden',
          border: '1px solid ' + primary + '22',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          background: '#fff',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Big image or gradient */}
          <div style={{
            height: 180,
            background: resolvePackageImage(pkg, branding)
              ? `url('${resolvePackageImage(pkg, branding)}') center/cover no-repeat`
              : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
            position: 'relative',
            display: 'flex', alignItems: 'flex-end',
            padding: 14,
          }}>
            {!resolvePackageImage(pkg, branding) && (
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 48, opacity: 0.25, color: '#fff',
              }}>⛳</div>
            )}
            {/* Bottom-left label overlay on the image */}
            <div style={{
              position: 'relative', zIndex: 1,
              padding: '4px 10px', borderRadius: 6,
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(4px)',
              ...bodyFontStyle(branding),
              fontSize: 10, fontWeight: 700, color: primary,
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
              {pkg.Nights}N / {pkg.Rounds}R / {pkg.NumGolfers || pkg.MaxGolfers || 2}G
            </div>
          </div>
          {/* Minimal info bar */}
          <div style={{
            padding: '12px 14px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 10, borderTop: '1px solid ' + primary + '11',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                ...headerFontStyle(branding),
                fontSize: 14, color: primary,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{pkg.PackageName}</div>
              <StartingFrom style={{ marginTop: 2 }} />
              <div style={{ fontSize: 16, fontWeight: 800, color: accent, lineHeight: 1 }}>
                {fmtMoney(pkg.PricePerPerson, { whole: true })}
                <span style={{ ...bodyFontStyle(branding), fontSize: 10, color: '#888', fontWeight: 400, marginLeft: 2 }}>{t('sp_per_golfer_short', '/Golfer')}</span>
              </div>
            </div>
            <span style={{ ...buttonStyle(branding, 'sm'), padding: '6px 12px', fontSize: 11, flexShrink: 0 }}>{t('sp_pw_book', 'Book')}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Layout 8: carousel — horizontal scroll strip ───
//
// Pure CSS: flex row with overflow-x auto and scroll snapping. Each card
// has a fixed width so you can always see multiple at once. Works with
// any number of packages — the merchant can drag-scroll through them.
function PreviewCarouselLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div>
      <div style={{
        display: 'flex',
        gap: 14,
        overflowX: 'auto',
        scrollSnapType: 'x mandatory',
        paddingBottom: 12,
        WebkitOverflowScrolling: 'touch',
      }}>
        {packages.map(pkg => (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
            flex: '0 0 280px',
            scrollSnapAlign: 'start',
            background: '#fff',
            border: '1px solid ' + primary + '33',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            {/* Image strip */}
            <div style={{
              height: 140,
              background: resolvePackageImage(pkg, branding)
                ? `url('${resolvePackageImage(pkg, branding)}') center/cover no-repeat`
                : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
              position: 'relative',
            }}>
              {!resolvePackageImage(pkg, branding) && (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 36, color: '#fff', opacity: 0.3,
                }}>⛳</div>
              )}
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', flex: 1 }}>
              <h4 style={{ ...headerFontStyle(branding), margin: '0 0 4px', fontSize: 15, color: primary }}>
                {pkg.PackageName}
              </h4>
              <div style={{
                ...bodyFontStyle(branding),
                fontSize: 11, color: '#64748b', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: 0.3,
              }}>
                {pkg.Nights}N · {pkg.Rounds}R · {t((pkg.NumGolfers || pkg.MaxGolfers || 2) === 1 ? 'sp_golfer_count_singular' : 'sp_golfers_count', `{count} golfer${(pkg.NumGolfers || pkg.MaxGolfers || 2) !== 1 ? 's' : ''}`, { count: pkg.NumGolfers || pkg.MaxGolfers || 2 })}
              </div>
              {pkg.Description && (
                <p style={{
                  ...bodyFontStyle(branding),
                  fontSize: 11, color: '#64748b',
                  margin: '0 0 10px', lineHeight: 1.4, flex: 1,
                }}>
                  {pkg.Description.length > 70 ? pkg.Description.slice(0, 70) + '...' : pkg.Description}
                </p>
              )}
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 'auto', paddingTop: 10,
                borderTop: '1px solid ' + primary + '11',
              }}>
                <div>
                  <StartingFrom />
                  <div style={{ fontSize: 20, fontWeight: 800, color: accent }}>
                    {fmtMoney(pkg.PricePerPerson, { whole: true })}
                    <span style={{ ...bodyFontStyle(branding), fontSize: 10, color: '#888', fontWeight: 400, marginLeft: 2 }}>{t('sp_per_golfer_short', '/Golfer')}</span>
                  </div>
                </div>
                <span style={{ ...buttonStyle(branding, 'sm'), padding: '6px 14px', fontSize: 11 }}>{t('sp_pw_book_now', 'Book Now')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Scroll hint when there are more packages than fit */}
      {packages.length > 3 && (
        <div style={{
          ...bodyFontStyle(branding),
          fontSize: 10, color: '#9ca3af', textAlign: 'center', marginTop: 4,
          fontStyle: 'italic',
        }}>
          ← Scroll to see more packages →
        </div>
      )}
    </div>
  )
}

// ─── Layout 9: matrix — feature comparison grid ───
//
// Different from Comparison (which has tall independent columns per
// package). Matrix builds a single grid where ROWS are inclusions and
// COLUMNS are packages, with ✓ / · in each cell. Lets the merchant's
// customer compare a specific feature across all tiers at a glance.
function PreviewMatrixLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  // Build the union of all inclusions across all packages, preserving
  // first-seen order
  const allInclusions = []
  const seen = new Set()
  for (const pkg of packages) {
    for (const inc of (pkg.inclusions || [])) {
      const key = inc.ItemName.toLowerCase().trim()
      if (!seen.has(key)) {
        seen.add(key)
        allInclusions.push(inc.ItemName)
      }
    }
  }

  const packageHas = (pkg, inclusion) => {
    return (pkg.inclusions || []).some(inc => inc.ItemName.toLowerCase().trim() === inclusion.toLowerCase().trim())
  }

  return (
    <div style={{
      border: '1px solid ' + primary + '33',
      borderRadius: 12,
      overflow: 'hidden',
      background: '#fff',
    }}>
      {/* Top header row: package names + prices */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${packages.length}, 1fr)`,
        background: primary,
        color: '#fff',
      }}>
        <div style={{ padding: '16px 18px', ...bodyFontStyle(branding), fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
          {t('sp_pw_whats_included', "What's included")}
        </div>
        {packages.map(pkg => (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
            padding: '16px 12px',
            textAlign: 'center',
            borderLeft: '1px solid rgba(255,255,255,0.15)',
          }}>
            <div style={{ ...headerFontStyle(branding), fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
              {pkg.PackageName}
            </div>
            <StartingFrom style={{ color: 'rgba(255,255,255,0.6)' }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
              {fmtMoney(pkg.PricePerPerson, { whole: true })}
            </div>
            <div style={{ ...bodyFontStyle(branding), fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>{t('sp_pw_per_person', 'per person')}</div>
            <TotalPriceHint pkg={pkg} branding={branding} color="rgba(255,255,255,0.6)" />
          </div>
        ))}
      </div>

      {/* Metadata row: nights/rounds */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${packages.length}, 1fr)`,
        background: primary + '08',
        borderBottom: '1px solid ' + primary + '22',
      }}>
        <div style={{ padding: '10px 18px', ...bodyFontStyle(branding), fontSize: 12, color: '#475569', fontWeight: 600 }}>
          {t('sp_pw_nights_rounds', 'Nights / Rounds')}
        </div>
        {packages.map(pkg => (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
            padding: '10px 12px',
            textAlign: 'center',
            ...bodyFontStyle(branding), fontSize: 12, color: '#475569',
            borderLeft: '1px solid ' + primary + '11',
          }}>
            {pkg.Nights}N / {pkg.Rounds}R
          </div>
        ))}
      </div>

      {/* Feature rows */}
      {allInclusions.map((inclusion, rowIdx) => (
        <div key={inclusion} style={{
          display: 'grid',
          gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${packages.length}, 1fr)`,
          background: rowIdx % 2 === 0 ? '#fff' : primary + '04',
          borderBottom: '1px solid ' + primary + '11',
        }}>
          <div style={{
            padding: '12px 18px',
            ...bodyFontStyle(branding),
            fontSize: 13, color: '#334155',
          }}>
            {inclusion}
          </div>
          {packages.map(pkg => (
            <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
              padding: '12px',
              textAlign: 'center',
              borderLeft: '1px solid ' + primary + '11',
              fontSize: 16,
            }}>
              {packageHas(pkg, inclusion)
                ? <span style={{ color: accent, fontWeight: 800 }}>✓</span>
                : <span style={{ color: '#cbd5e1' }}>·</span>}
            </div>
          ))}
        </div>
      ))}

      {/* Book row — anchored footer */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `minmax(180px, 1.5fr) repeat(${packages.length}, 1fr)`,
        background: '#fff',
        borderTop: '2px solid ' + primary + '33',
      }}>
        <div style={{ padding: '16px 18px' }} />
        {packages.map(pkg => (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO} style={{
            padding: '16px 12px',
            textAlign: 'center',
            borderLeft: '1px solid ' + primary + '11',
          }}>
            <span style={{ ...buttonStyle(branding, 'sm'), padding: '8px 14px', fontSize: 11 }}>
              {t('sp_pw_book_now', 'Book Now')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Layout 10: split — alternating image-left / image-right editorial rows ───
function PreviewSplitLayout({ packages, branding }) {
  const { t } = useLanguage()
  const { primary, accent } = resolveColors(branding)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {packages.map((pkg, idx) => {
        const imageOnLeft = idx % 2 === 0
        const isLast = idx === packages.length - 1
        const imagePane = (
          <div style={{
            height: 240,
            background: resolvePackageImage(pkg, branding)
              ? `url('${resolvePackageImage(pkg, branding)}') center/cover no-repeat`
              : `linear-gradient(135deg, ${primary} 0%, ${primary}cc 100%)`,
            borderRadius: 10,
            position: 'relative',
            display: 'flex', alignItems: 'flex-end', padding: 16,
          }}>
            {!resolvePackageImage(pkg, branding) && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 72, opacity: 0.25, color: '#fff',
              }}>⛳</div>
            )}
          </div>
        )
        const contentPane = (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 8px' }}>
            <h3 style={{
              ...headerFontStyle(branding),
              margin: '0 0 8px',
              fontSize: 26,
              color: primary,
              lineHeight: 1.15,
            }}>
              {pkg.PackageName}
            </h3>
            <div style={{
              ...bodyFontStyle(branding),
              fontSize: 12, color: '#64748b',
              marginBottom: 12,
              textTransform: 'uppercase', letterSpacing: 0.3,
            }}>
              {pkgMeta(t, pkg, { golfers: true, upper: true })}
            </div>
            {pkg.Description && (
              <p style={{
                ...bodyFontStyle(branding),
                fontSize: 13, color: '#475569',
                margin: '0 0 14px', lineHeight: 1.6,
              }}>
                {pkg.Description.length > 180 ? pkg.Description.slice(0, 180) + '...' : pkg.Description}
              </p>
            )}
            {pkg.inclusions?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
                {pkg.inclusions.slice(0, 5).map((inc, i) => (
                  <span key={i} style={inclusionPillStyle(branding)}>✓ {inc.ItemName}</span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={buttonStyle(branding, 'md')}>{t('sp_pw_book_now', 'Book Now')}</span>
              <div>
                <StartingFrom />
                <span style={{ fontSize: 28, fontWeight: 800, color: accent, lineHeight: 1 }}>
                  {fmtMoney(pkg.PricePerPerson, { whole: true })}
                </span>
                <span style={{ ...bodyFontStyle(branding), fontSize: 11, color: '#888', marginLeft: 4 }}>
                  {t('sp_pw_per_person', 'per person')}
                </span>
              </div>
              <TotalPriceHint pkg={pkg} branding={branding} />
            </div>
          </div>
        )
        return (
          <div key={pkg.ID_NO} data-pkg-id={pkg.ID_NO}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 24,
              alignItems: 'stretch',
              padding: '28px 0',
            }}>
              {imageOnLeft ? imagePane : contentPane}
              {imageOnLeft ? contentPane : imagePane}
            </div>
            {/* Divider between packages */}
            {!isLast && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div style={{ flex: 1, height: 1, background: primary + '33' }} />
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: primary + '44' }} />
                <div style={{ flex: 1, height: 1, background: primary + '33' }} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Styles ───
const cardStyle = { padding: 24, marginBottom: 20 }
const sectionTitleStyle = { margin: '0 0 6px', fontSize: 16, color: 'var(--secondary-950)' }
const hintStyle = { margin: '0 0 14px', fontSize: 12, color: 'var(--text-muted)' }
const fieldLabelStyle = { display: 'block', fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }
const textInputStyle = {
  flex: 1, width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--card-border)', fontSize: 13,
}
const pickerCardStyle = {
  cursor: 'pointer', padding: '12px 8px', borderRadius: 8, border: '2px solid',
  background: '#fff', textAlign: 'center', transition: 'all 0.15s',
}

const errorBoxStyle = {
  padding: '10px 14px', marginBottom: 16,
  background: '#fef2f2', color: '#dc2626',
  border: '1px solid #fecaca', borderRadius: 8, fontSize: 13,
}

const extractBoxStyle = {
  marginBottom: 20, padding: 16,
  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
}
const extractInputStyle = {
  flex: 1, padding: '10px 12px',
  border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13, background: '#fff',
}
const extractBtnStyle = {
  padding: '10px 18px', background: '#92400e', color: '#fff',
  border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13,
  cursor: 'pointer', whiteSpace: 'nowrap',
}
const extractErrStyle = {
  marginTop: 10, padding: '8px 12px', background: '#fef2f2', color: '#991b1b',
  borderRadius: 6, fontSize: 12, border: '1px solid #fca5a5',
}
const extractOkStyle = {
  marginTop: 10, padding: '10px 12px',
  background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 12,
}
